import React from 'react';
import { Column, Row } from 'react-table';
import { RoleService } from 'Services';
import { getLinkedRulesCount, OnClickEvent, KeyEvent } from 'Utils';
import { useGetColumnConfig } from 'Components/Table/utils';
import LinkRulesCount from '../LinkedRulesCount';
import MissingCustomListError from '../MissingCustomListError';

function useGetListTableColumns(onTrashIconClick: <T>(id?: number, event?: OnClickEvent<T> | KeyEvent<T>) => void) {
    const { getColConfig } = useGetColumnConfig<ListManagementApiList>();

    /**
     * The width for the columns is based on a ratio
     * I'm trying to get as close as possible to the design ratio
     * while also trying to maintain a minimum width
     *
     * This is an art and not a science at this point.
     */
    const columns = React.useMemo(() => {
        const regularColumns: Column<ListManagementApiList>[] = [
            {
                Header: () => null,
                accessor: '_formatted',
                width: 5 / 3,
                disableSortBy: true,
                renderType: 'overrideCell',
                Cell: ({ row }: { row: Row }) => {
                    return row?.original?.customListType?.listType ? (
                        <MissingCustomListError customListType={row?.original?.customListType} />
                    ) : null;
                }
            },

            {
                Header: 'List Name',
                accessor: 'name',
                id: 'name',
                dataType: 'text',
                width: 424 / 2
            },
            {
                Header: 'List Type',
                accessor: (d) => d._formatted.listType,
                id: '_formatted.listType',
                dataType: 'text',
                minWidth: 150,
                width: 256 / 2
            },
            {
                Header: 'List Entries',
                id: '_formatted.activeListEntriesCount',
                minWidth: 100,
                dataType: 'number',
                accessor: (d) => d?._formatted?.activeListEntriesCount,
                width: 112 / 2
            },
            {
                Header: 'Created By',
                id: '_formatted.createdBy',
                accessor: (d) => d?._formatted?.createdBy,
                dataType: 'text',
                width: 168 / 2
            },
            {
                Header: 'Last Modified',
                accessor: 'modifiedAt',
                id: '_formatted.modifiedAt',
                dataType: 'datetime',
                width: 184 / 2
            },
            // specifically for list management so this should be in here isntead of Table/Cell
            {
                Header: 'Linked Rules',
                accessor: (d) => d?._formatted.linkedRulesCount,
                id: '_formatted.linkedRulesCount',
                dataType: 'number',
                minWidth: 100,
                maxWidth: 100,
                renderType: 'overrideCell',
                Cell: ({ row }: any) => {
                    return (
                        <LinkRulesCount
                            sandboxRules={row.original.sandboxRules}
                            deployedRules={row.original.deployedRules}
                        />
                    );
                }
            }
        ];

        if (RoleService.isListAdmin()) {
            regularColumns.push({
                Header: () => null,
                accessor: 'id',
                id: 'trash',
                disableSortBy: true,
                width: 30,
                minWidth: 30,
                renderType: 'trashIcon',
                renderOptions: (d) => {
                    return {
                        onClick: onTrashIconClick,
                        // hide if there's a linked rule to the list
                        hideTrash: !!getLinkedRulesCount(d.sandboxRules, d.deployedRules)
                    };
                }
            });
        }

        // TS has an issue if we try to map
        // with the above array so we need to do it like this
        return regularColumns.map(getColConfig);
    }, [getColConfig, onTrashIconClick]);

    return columns;
}

export default useGetListTableColumns;
